// ─────────────────────────────────────────────────────────────
// MetadataChip — renders acquisition metadata for an image.
//
// Compact mode: single line summary ("26 nm/px • 3 kV • LVD SE").
// Expandable mode: same summary + chevron that opens MetadataDetailsPanel.
//
// Image Library cards use the expandable variant.
// The Python tab uses MetadataDetailsPanel directly inside a Popover
// (see PythonTab.tsx) — a small (i) button next to the magnifying glass
// opens the popover with the full panel content.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Collapse,
  Stack,
  Divider,
  CircularProgress,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  imageApi,
  type AcquisitionMetadata,
  type MetadataSource,
  type UserImage,
} from "../../api/client";

interface MetadataChipProps {
  metadata?: AcquisitionMetadata | null;
  source?: MetadataSource | null;
  /** When provided, enables the Details disclosure with raw-FEI fetch + Edit button. */
  imageId?: number;
  /** Image fields — passed through to the viewer dialog. */
  imageName?: string;
  imageDescription?: string;
  imageTags?: string | null;
  /** Compact: single-line summary only, no expand affordance. */
  compact?: boolean;
  /** Called after the user saves edits, with the full updated UserImage. */
  onImageChanged?: (updated: UserImage) => void;
}

export function MetadataChip({ metadata, source, imageId, imageName, imageDescription, imageTags, compact = false, onImageChanged }: MetadataChipProps) {
  const [open, setOpen] = useState(false);

  const hasMetadata = metadata && source && source !== "none";

  if (!hasMetadata) {
    return (
      <Tooltip title="No acquisition metadata detected. Measurements will be in pixels unless you add a pixel size manually.">
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            color: "warning.main",
            fontStyle: "italic",
            display: "inline-block",
          }}
        >
          ⚠ No metadata
        </Typography>
      </Tooltip>
    );
  }

  const summary = buildSummary(metadata!);
  const sourceLabel = labelForSource(source!);
  const expandable = !compact && imageId !== undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
        <Tooltip title={`Source: ${sourceLabel}`} placement="top">
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              color: "text.secondary",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "monospace",
            }}
          >
            {summary}
          </Typography>
        </Tooltip>
        <SourceBadge source={source!} />
        {expandable && (
          <Tooltip title={open ? "Hide details" : "Show details"}>
            <IconButton
              size="small"
              onClick={() => setOpen((o) => !o)}
              sx={{ p: 0.25 }}
              aria-label={open ? "Hide image metadata details" : "Show image metadata details"}
            >
              <ExpandMoreOutlinedIcon
                sx={{
                  fontSize: 14,
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {expandable && (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 0.75 }}>
            <MetadataDetailsPanel
              metadata={metadata!}
              source={source!}
              imageId={imageId}
              imageName={imageName}
              imageDescription={imageDescription}
              imageTags={imageTags}
              onImageChanged={onImageChanged}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// MetadataDetailsPanel — the labeled detail rows + raw INI viewer.
// Standalone so it can be embedded in popovers, dialogs, or the
// inline Collapse used by MetadataChip's expandable mode.
// ─────────────────────────────────────────────────────────────

interface MetadataDetailsPanelProps {
  metadata?: AcquisitionMetadata | null;
  source?: MetadataSource | null;
  imageId?: number;
  /** Image fields used by the dialog header + identity-edit form. */
  imageName?: string;
  imageDescription?: string;
  imageTags?: string | null;
  /** Optional width hint — defaults to 100% of container. */
  width?: number | string;
  /**
   * Called whenever content changes height (e.g. dialog open/close affects nothing
   * in-panel today, but the prop is preserved for the Python tab popover anchor).
   */
  onResize?: () => void;
  /** Called after a successful save with the full updated UserImage. */
  onImageChanged?: (updated: UserImage) => void;
}

export function MetadataDetailsPanel({
  metadata,
  source,
  imageId,
  imageName,
  imageDescription,
  imageTags,
  width,
  onResize,
  onImageChanged,
}: MetadataDetailsPanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"view" | "edit">("view");

  // Re-anchor any wrapping popover if the panel structure changes.
  useEffect(() => {
    onResize?.();
  }, [onResize, metadata, source]);

  function openDialog(mode: "view" | "edit") {
    setDialogMode(mode);
    setDialogOpen(true);
  }

  const hasMetadata = metadata && source && source !== "none";

  if (!hasMetadata) {
    return (
      <>
        <Box
          sx={{
            p: 1.25,
            bgcolor: "action.hover",
            borderRadius: 0.5,
            width: width ?? "100%",
          }}
        >
          <Typography variant="caption" color="warning.main" sx={{ fontStyle: "italic", fontSize: "0.7rem", display: "block" }}>
            ⚠ No acquisition metadata detected for this image.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, fontSize: "0.65rem" }}>
            Measurements will be reported in pixels unless you add a pixel size manually.
          </Typography>
          {imageId !== undefined && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => openDialog("edit")}
              sx={{ mt: 1, fontSize: "0.65rem", py: 0.25 }}
            >
              Add metadata manually
            </Button>
          )}
        </Box>
        {imageId !== undefined && (
          <MetadataViewerDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            imageId={imageId}
            imageName={imageName ?? "Image"}
            imageDescription={imageDescription ?? ""}
            imageTags={imageTags ?? null}
            metadata={metadata ?? null}
            source={source ?? "none"}
            startInEditMode={dialogMode === "edit"}
            onImageChanged={onImageChanged}
          />
        )}
      </>
    );
  }

  const sourceLabel = labelForSource(source!);

  return (
    <>
      <Box
        sx={{
          p: 1.25,
          bgcolor: "action.hover",
          borderRadius: 0.5,
          fontSize: "0.7rem",
          width: width ?? "100%",
        }}
      >
        <Stack spacing={0.5}>
          {renderField("Pixel size", formatPixelSize(metadata!.pixel_size_nm))}
          {renderField("Field of view", formatFOV(metadata!.hfw_um, metadata!.vfw_um))}
          {renderField("Voltage", metadata!.kV !== undefined ? `${formatNum(metadata!.kV)} kV` : null)}
          {renderField("Beam current", metadata!.beam_current_pA !== undefined ? `${formatNum(metadata!.beam_current_pA)} pA` : null)}
          {renderField("Dwell time", metadata!.dwell_us !== undefined ? `${formatNum(metadata!.dwell_us)} µs` : null)}
          {renderField("Working distance", metadata!.working_distance_mm !== undefined ? `${formatNum(metadata!.working_distance_mm)} mm` : null)}
          {renderField(
            "Detector",
            metadata!.detector
              ? `${metadata!.detector}${metadata!.signal ? ` (${metadata!.signal})` : ""}`
              : null,
          )}
          {renderField("Instrument", metadata!.instrument)}
          {renderField("Acquired", metadata!.date_acquired)}
          {renderField("Source", sourceLabel)}
        </Stack>

        {imageId !== undefined && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: "flex" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<InfoOutlinedIcon sx={{ fontSize: 13 }} />}
                onClick={() => openDialog("view")}
                sx={{ fontSize: "0.65rem", py: 0.25, px: 0.75, textTransform: "none" }}
              >
                Image details
              </Button>
            </Box>
          </>
        )}
      </Box>

      {imageId !== undefined && (
        <MetadataViewerDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          imageId={imageId}
          imageName={imageName ?? "Image"}
          imageDescription={imageDescription ?? ""}
          imageTags={imageTags ?? null}
          metadata={metadata ?? null}
          source={source ?? "none"}
          startInEditMode={dialogMode === "edit"}
          onImageChanged={onImageChanged}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MetadataHoverSummary — small inline summary with a rich
// tooltip on hover. Used on Image Library cards to give a fast
// metadata snapshot without opening the full viewer dialog.
// Read-only by design — to edit, the user opens the dialog via
// the "View image details" button.
// ─────────────────────────────────────────────────────────────

interface MetadataHoverSummaryProps {
  metadata?: AcquisitionMetadata | null;
  source?: MetadataSource | null;
}

export function MetadataHoverSummary({ metadata, source }: MetadataHoverSummaryProps) {
  const hasMetadata = metadata && source && source !== "none";

  if (!hasMetadata) {
    return (
      <Tooltip
        title="No acquisition metadata. Open View image details to add a pixel size manually so measurements are calibrated."
        placement="top"
        arrow
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            color: "warning.main",
            fontStyle: "italic",
            display: "inline-flex",
            alignItems: "center",
            cursor: "help",
          }}
        >
          ⚠ No metadata
        </Typography>
      </Tooltip>
    );
  }

  const md = metadata!;
  const summary = buildSummary(md);
  const sourceLabel = labelForSource(source!);

  const tooltipContent = (
    <Box sx={{ p: 0.5, minWidth: 220 }}>
      <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600, display: "block", mb: 0.5 }}>
        Acquisition snapshot
      </Typography>
      <Stack spacing={0.25}>
        {hoverField("Pixel size", formatPixelSize(md.pixel_size_nm))}
        {hoverField("Field of view", formatFOV(md.hfw_um, md.vfw_um))}
        {hoverField("Voltage", md.kV !== undefined ? `${formatNum(md.kV)} kV` : null)}
        {hoverField("Beam current", md.beam_current_pA !== undefined ? `${formatNum(md.beam_current_pA)} pA` : null)}
        {hoverField("Dwell time", md.dwell_us !== undefined ? `${formatNum(md.dwell_us)} µs` : null)}
        {hoverField("Working distance", md.working_distance_mm !== undefined ? `${formatNum(md.working_distance_mm)} mm` : null)}
        {hoverField("Detector", md.detector ? `${md.detector}${md.signal ? ` (${md.signal})` : ""}` : null)}
        {hoverField("Instrument", md.instrument)}
      </Stack>
      <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.6)", display: "block", mt: 0.75, fontStyle: "italic" }}>
        Source: {sourceLabel}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          maxWidth: "100%",
          cursor: "help",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            color: "text.secondary",
            fontFamily: "monospace",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {summary}
        </Typography>
        <SourceBadge source={source!} />
      </Box>
    </Tooltip>
  );
}

function hoverField(label: string, value: string | null | undefined) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
      <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: "0.65rem", fontFamily: "monospace" }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// MetadataViewerDialog — dedicated full-size viewer/editor.
// Replaces the old inline raw-INI expansion + edit-mode swap.
// One surface for browsing labeled fields, reading raw FEI INI,
// and editing user-supplied metadata. Used by Image Library cards
// and the Python tab popover.
// ─────────────────────────────────────────────────────────────

interface MetadataViewerDialogProps {
  open: boolean;
  onClose: () => void;
  imageId: number;
  imageName: string;
  imageDescription: string;
  imageTags: string | null;
  metadata: AcquisitionMetadata | null;
  source: MetadataSource;
  startInEditMode?: boolean;
  /** Called after a successful save with the full updated UserImage. */
  onImageChanged?: (updated: UserImage) => void;
}

export function MetadataViewerDialog({
  open,
  onClose,
  imageId,
  imageName,
  imageDescription,
  imageTags,
  metadata,
  source,
  startInEditMode = false,
  onImageChanged,
}: MetadataViewerDialogProps) {
  const [mode, setMode] = useState<"view" | "edit">(startInEditMode ? "edit" : "view");
  const [rawIni, setRawIni] = useState<string | null>(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  // Identity edit state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");

  // Acquisition edit state (string-typed so users can clear with backspace; parsed at save)
  const [pixelSize, setPixelSize] = useState("");
  const [hfw, setHfw] = useState("");
  const [vfw, setVfw] = useState("");
  const [kV, setKV] = useState("");
  const [beamCurrent, setBeamCurrent] = useState("");
  const [dwell, setDwell] = useState("");
  const [workingDistance, setWorkingDistance] = useState("");
  const [detector, setDetector] = useState("");
  const [signal, setSignal] = useState("");
  const [instrument, setInstrument] = useState("");
  const [dateAcquired, setDateAcquired] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset state whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setMode(startInEditMode ? "edit" : "view");
    setSaveError(null);
    setSaving(false);
    setName(imageName);
    setDescription(imageDescription);
    setTags(imageTags ?? "");
    setPixelSize(numToStr(metadata?.pixel_size_nm));
    setHfw(numToStr(metadata?.hfw_um));
    setVfw(numToStr(metadata?.vfw_um));
    setKV(numToStr(metadata?.kV));
    setBeamCurrent(numToStr(metadata?.beam_current_pA));
    setDwell(numToStr(metadata?.dwell_us));
    setWorkingDistance(numToStr(metadata?.working_distance_mm));
    setDetector(metadata?.detector ?? "");
    setSignal(metadata?.signal ?? "");
    setInstrument(metadata?.instrument ?? "");
    setDateAcquired(metadata?.date_acquired ?? "");
  }, [open, imageId, imageName, imageDescription, imageTags, metadata, startInEditMode]);

  // Lazy-fetch raw FEI INI text once per dialog-open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRawLoading(true);
    setRawError(null);
    imageApi
      .getMetadata(imageId)
      .then((res) => {
        if (!cancelled) setRawIni(res.raw_fei_ini ?? "");
      })
      .catch((err) => {
        if (!cancelled) setRawError(err instanceof Error ? err.message : "Failed to load raw metadata");
      })
      .finally(() => {
        if (!cancelled) setRawLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, imageId]);

  // Fetch thumbnail blob URL on open; revoke on close
  useEffect(() => {
    if (!open) return;
    let url: string | null = null;
    let cancelled = false;
    imageApi
      .getThumbnailUrl(imageId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
        } else {
          url = u;
          setThumbUrl(u);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      setThumbUrl(null);
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, imageId]);

  function buildMetadataPayload(): AcquisitionMetadata {
    const out: AcquisitionMetadata = {};
    const px = strToNum(pixelSize);
    if (px !== undefined) out.pixel_size_nm = px;
    const h = strToNum(hfw);
    if (h !== undefined) out.hfw_um = h;
    const v = strToNum(vfw);
    if (v !== undefined) out.vfw_um = v;
    const k = strToNum(kV);
    if (k !== undefined) out.kV = k;
    const bc = strToNum(beamCurrent);
    if (bc !== undefined) out.beam_current_pA = bc;
    const d = strToNum(dwell);
    if (d !== undefined) out.dwell_us = d;
    const wd = strToNum(workingDistance);
    if (wd !== undefined) out.working_distance_mm = wd;
    if (detector.trim()) out.detector = detector.trim();
    if (signal.trim()) out.signal = signal.trim();
    if (instrument.trim()) out.instrument = instrument.trim();
    if (dateAcquired.trim()) out.date_acquired = dateAcquired.trim();
    return out;
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);

    const newMetadata = buildMetadataPayload();
    const initialMetadata: AcquisitionMetadata = metadata ?? {};
    const metadataChanged = !shallowEqualMeta(newMetadata, initialMetadata);

    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    const trimmedTags = tags.trim();
    const identityChanged =
      trimmedName !== imageName.trim() ||
      trimmedDesc !== imageDescription.trim() ||
      trimmedTags !== (imageTags ?? "").trim();

    // Client-side validation matches backend constraints
    if (identityChanged) {
      if (!trimmedName) {
        setSaveError("Image name cannot be empty.");
        setSaving(false);
        return;
      }
      if (trimmedDesc.length < 10) {
        setSaveError("Description must be at least 10 characters.");
        setSaving(false);
        return;
      }
    }

    let result: UserImage | null = null;
    try {
      if (identityChanged) {
        result = await imageApi.updateImage(imageId, {
          name: trimmedName,
          description: trimmedDesc,
          tags: trimmedTags || null,
        });
      }
      if (metadataChanged) {
        result = await imageApi.updateMetadata(imageId, newMetadata);
      }
      if (result) onImageChanged?.(result);
      setMode("view");
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // Predict source change for the user — same logic as the backend
  const wouldBeSource: MetadataSource = (() => {
    const payload = buildMetadataPayload();
    const initialDict: AcquisitionMetadata = metadata ?? {};
    if (Object.keys(payload).length === 0) return "none";
    if (shallowEqualMeta(payload, initialDict)) return source;
    if (source === "none" || source === "manual") return "manual";
    return "partial";
  })();

  const sourceWillChange = mode === "edit" && wouldBeSource !== source;
  const sourceLabel = labelForSource(source);
  const hasMetadata = metadata && source !== "none";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1, gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: "1.05rem", fontWeight: 600, lineHeight: 1.3 }}>
            {imageName}
          </Typography>
          {imageDescription && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "0.8rem",
                mt: 0.25,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {imageDescription}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ flexShrink: 0 }}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
          {/* ── LEFT: thumbnail + acquisition details (or edit form) ── */}
          <Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Thumbnail */}
            <Box
              sx={{
                width: "100%",
                aspectRatio: "3 / 2",
                bgcolor: "action.hover",
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {thumbUrl ? (
                <Box component="img" src={thumbUrl} alt={imageName}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <CircularProgress size={20} sx={{ opacity: 0.5 }} />
              )}
            </Box>

            {mode === "view" ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}>
                  Acquisition details
                </Typography>
                {!hasMetadata ? (
                  <Alert severity="warning" sx={{ fontSize: "0.75rem" }}>
                    No acquisition metadata recorded for this image. Click <strong>Edit</strong> below to add it manually.
                  </Alert>
                ) : (
                  <Stack spacing={0.5} sx={{ fontSize: "0.8rem" }}>
                    {renderField("Pixel size", formatPixelSize(metadata!.pixel_size_nm), "0.8rem")}
                    {renderField("Field of view", formatFOV(metadata!.hfw_um, metadata!.vfw_um), "0.8rem")}
                    {renderField("Voltage", metadata!.kV !== undefined ? `${formatNum(metadata!.kV)} kV` : null, "0.8rem")}
                    {renderField("Beam current", metadata!.beam_current_pA !== undefined ? `${formatNum(metadata!.beam_current_pA)} pA` : null, "0.8rem")}
                    {renderField("Dwell time", metadata!.dwell_us !== undefined ? `${formatNum(metadata!.dwell_us)} µs` : null, "0.8rem")}
                    {renderField("Working distance", metadata!.working_distance_mm !== undefined ? `${formatNum(metadata!.working_distance_mm)} mm` : null, "0.8rem")}
                    {renderField(
                      "Detector",
                      metadata!.detector ? `${metadata!.detector}${metadata!.signal ? ` (${metadata!.signal})` : ""}` : null,
                      "0.8rem",
                    )}
                    {renderField("Instrument", metadata!.instrument, "0.8rem")}
                    {renderField("Acquired", metadata!.date_acquired, "0.8rem")}
                    {renderField("Source", sourceLabel, "0.8rem")}
                  </Stack>
                )}
                {imageTags && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                      Tags:&nbsp;
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.7rem", fontFamily: "monospace" }}>
                      {imageTags}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Identity edit */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}>
                    Image details
                  </Typography>
                  <Stack spacing={1.25}>
                    <TextField label="Name" size="small" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
                    <TextField
                      label="Description"
                      size="small"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={2}
                      required
                      fullWidth
                      error={description.length > 0 && description.trim().length < 10}
                      helperText={description.trim().length < 10 ? `${description.trim().length}/10 characters minimum` : undefined}
                    />
                    <TextField
                      label="Tags"
                      size="small"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="comma, separated"
                      fullWidth
                    />
                  </Stack>
                </Box>

                <Divider />

                {/* Acquisition edit */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.5 }}>
                    Acquisition details
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5, fontSize: "0.7rem" }}>
                    Pixel size is the key field for calibrated measurements. Everything else is optional context for Marie.
                  </Typography>
                  <Stack spacing={1.25}>
                    <NumField label="Pixel size" unit="nm/px" value={pixelSize} onChange={setPixelSize} required />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                      <NumField label="HFW" unit="µm" value={hfw} onChange={setHfw} />
                      <NumField label="VFW" unit="µm" value={vfw} onChange={setVfw} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                      <NumField label="Voltage" unit="kV" value={kV} onChange={setKV} />
                      <NumField label="Beam current" unit="pA" value={beamCurrent} onChange={setBeamCurrent} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                      <NumField label="Dwell" unit="µs" value={dwell} onChange={setDwell} />
                      <NumField label="WD" unit="mm" value={workingDistance} onChange={setWorkingDistance} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                      <TextField label="Detector" size="small" value={detector} onChange={(e) => setDetector(e.target.value)} />
                      <TextField label="Signal" size="small" value={signal} onChange={(e) => setSignal(e.target.value)} />
                    </Box>
                    <TextField label="Instrument" size="small" value={instrument} onChange={(e) => setInstrument(e.target.value)} />
                    <TextField label="Acquired" size="small" value={dateAcquired} onChange={(e) => setDateAcquired(e.target.value)} />
                  </Stack>
                </Box>

                {sourceWillChange && (
                  <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
                    Acquisition source will be marked as <strong>{labelForSource(wouldBeSource)}</strong>.
                  </Alert>
                )}
                {saveError && (
                  <Alert severity="error" sx={{ fontSize: "0.75rem" }}>
                    {saveError}
                  </Alert>
                )}
              </Stack>
            )}
          </Box>

          {/* ── RIGHT: raw FEI INI ─────────────────────────────── */}
          <Box sx={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}>
              Raw metadata (FEI INI)
            </Typography>
            {rawLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                <CircularProgress size={14} />
                <Typography variant="caption">Loading raw metadata…</Typography>
              </Box>
            ) : rawError ? (
              <Alert severity="error" sx={{ fontSize: "0.75rem" }}>
                {rawError}
              </Alert>
            ) : rawIni ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 320,
                  p: 1.5,
                  bgcolor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0.5,
                  maxHeight: { xs: 360, md: 600 },
                  overflow: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.7rem",
                  whiteSpace: "pre",
                  lineHeight: 1.5,
                }}
              >
                {rawIni}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "background.default",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 0.5,
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No raw metadata available for this image (not a TF FEI TIFF).
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        {mode === "view" ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setMode("edit")}>
              Edit
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => (hasMetadata ? setMode("view") : onClose())} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// MetadataEditForm — user-supplied acquisition metadata editor.
// Used in two contexts: the upload modal "no metadata detected"
// flow, and the Edit affordance inside MetadataDetailsPanel.
// Pixel size is the only field the user really needs to set for
// calibrated measurements; everything else is optional reasoning
// context for Marie.
// ─────────────────────────────────────────────────────────────

interface MetadataEditFormProps {
  imageId: number;
  initial: AcquisitionMetadata | null;
  currentSource: MetadataSource;
  onSaved: (next: { metadata: AcquisitionMetadata | null; source: MetadataSource }) => void;
  onCancel: () => void;
  width?: number | string;
}

export function MetadataEditForm({
  imageId,
  initial,
  currentSource,
  onSaved,
  onCancel,
  width,
}: MetadataEditFormProps) {
  // Each field as a string so the user can clear a value with backspace.
  // Numbers are parsed at submit time.
  const [pixelSize, setPixelSize] = useState(numToStr(initial?.pixel_size_nm));
  const [hfw, setHfw] = useState(numToStr(initial?.hfw_um));
  const [vfw, setVfw] = useState(numToStr(initial?.vfw_um));
  const [kV, setKV] = useState(numToStr(initial?.kV));
  const [beamCurrent, setBeamCurrent] = useState(numToStr(initial?.beam_current_pA));
  const [dwell, setDwell] = useState(numToStr(initial?.dwell_us));
  const [workingDistance, setWorkingDistance] = useState(numToStr(initial?.working_distance_mm));
  const [detector, setDetector] = useState(initial?.detector ?? "");
  const [signal, setSignal] = useState(initial?.signal ?? "");
  const [instrument, setInstrument] = useState(initial?.instrument ?? "");
  const [dateAcquired, setDateAcquired] = useState(initial?.date_acquired ?? "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function buildPayload(): AcquisitionMetadata {
    const out: AcquisitionMetadata = {};
    const px = strToNum(pixelSize);
    if (px !== undefined) out.pixel_size_nm = px;
    const h = strToNum(hfw);
    if (h !== undefined) out.hfw_um = h;
    const v = strToNum(vfw);
    if (v !== undefined) out.vfw_um = v;
    const k = strToNum(kV);
    if (k !== undefined) out.kV = k;
    const bc = strToNum(beamCurrent);
    if (bc !== undefined) out.beam_current_pA = bc;
    const d = strToNum(dwell);
    if (d !== undefined) out.dwell_us = d;
    const wd = strToNum(workingDistance);
    if (wd !== undefined) out.working_distance_mm = wd;
    if (detector.trim()) out.detector = detector.trim();
    if (signal.trim()) out.signal = signal.trim();
    if (instrument.trim()) out.instrument = instrument.trim();
    if (dateAcquired.trim()) out.date_acquired = dateAcquired.trim();
    return out;
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    const payload = buildPayload();
    try {
      const updated = await imageApi.updateMetadata(imageId, payload);
      onSaved({
        metadata: updated.acquisition_metadata ?? null,
        source: (updated.metadata_source ?? "none") as MetadataSource,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save metadata");
      setSaving(false);
    }
  }

  // Predict source change for the user — same logic as the backend.
  const wouldBeSource: MetadataSource = (() => {
    const payload = buildPayload();
    const initialDict: AcquisitionMetadata = initial ?? {};
    if (Object.keys(payload).length === 0) return "none";
    if (shallowEqualMeta(payload, initialDict)) return currentSource;
    if (currentSource === "none" || currentSource === "manual") return "manual";
    return "partial";
  })();

  const sourceWillChange = wouldBeSource !== currentSource;

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "action.hover",
        borderRadius: 0.5,
        width: width ?? "100%",
        fontSize: "0.7rem",
      }}
    >
      <Typography variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 600, display: "block", mb: 0.25 }}>
        Edit acquisition metadata
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", display: "block", mb: 1 }}>
        Pixel size is the key field for calibrated measurements. Everything else is optional context for Marie.
      </Typography>

      <Stack spacing={1}>
        <NumField label="Pixel size" unit="nm/px" value={pixelSize} onChange={setPixelSize} required />
        <NumField label="Horizontal field width" unit="µm" value={hfw} onChange={setHfw} />
        <NumField label="Vertical field width" unit="µm" value={vfw} onChange={setVfw} />
        <NumField label="Voltage" unit="kV" value={kV} onChange={setKV} />
        <NumField label="Beam current" unit="pA" value={beamCurrent} onChange={setBeamCurrent} />
        <NumField label="Dwell time" unit="µs" value={dwell} onChange={setDwell} />
        <NumField label="Working distance" unit="mm" value={workingDistance} onChange={setWorkingDistance} />
        <TextField
          label="Detector"
          size="small"
          value={detector}
          onChange={(e) => setDetector(e.target.value)}
          inputProps={{ style: { fontSize: "0.7rem" } }}
          InputLabelProps={{ style: { fontSize: "0.7rem" } }}
        />
        <TextField
          label="Signal"
          size="small"
          value={signal}
          onChange={(e) => setSignal(e.target.value)}
          inputProps={{ style: { fontSize: "0.7rem" } }}
          InputLabelProps={{ style: { fontSize: "0.7rem" } }}
        />
        <TextField
          label="Instrument"
          size="small"
          value={instrument}
          onChange={(e) => setInstrument(e.target.value)}
          inputProps={{ style: { fontSize: "0.7rem" } }}
          InputLabelProps={{ style: { fontSize: "0.7rem" } }}
        />
        <TextField
          label="Acquired"
          size="small"
          value={dateAcquired}
          onChange={(e) => setDateAcquired(e.target.value)}
          inputProps={{ style: { fontSize: "0.7rem" } }}
          InputLabelProps={{ style: { fontSize: "0.7rem" } }}
        />
      </Stack>

      {sourceWillChange && (
        <Alert severity="info" sx={{ mt: 1, py: 0.25, fontSize: "0.65rem" }}>
          Source will be marked as <strong>{labelForSource(wouldBeSource)}</strong>.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0.25, fontSize: "0.65rem" }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 1.5, justifyContent: "flex-end" }}>
        <Button size="small" onClick={onCancel} disabled={saving} sx={{ fontSize: "0.65rem" }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={10} color="inherit" /> : null}
          sx={{ fontSize: "0.65rem" }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>
    </Box>
  );
}

function NumField({
  label,
  unit,
  value,
  onChange,
  required,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <TextField
      label={`${label} (${unit})${required ? " *" : ""}`}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type="text"
      inputMode="decimal"
      inputProps={{ style: { fontSize: "0.7rem" } }}
      InputLabelProps={{ style: { fontSize: "0.7rem" } }}
    />
  );
}

function numToStr(n: number | undefined): string {
  return n === undefined || n === null ? "" : String(n);
}

function strToNum(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  return isNaN(n) ? undefined : n;
}

function shallowEqualMeta(a: AcquisitionMetadata, b: AcquisitionMetadata): boolean {
  const ka = Object.keys(a) as (keyof AcquisitionMetadata)[];
  const kb = Object.keys(b) as (keyof AcquisitionMetadata)[];
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k]);
}

// ── Helpers ───────────────────────────────────────────────────

function renderField(label: string, value: string | null | undefined, fontSize: string = "0.65rem") {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
      <Typography variant="caption" sx={{ fontSize, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontSize, fontFamily: "monospace", textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function buildSummary(md: AcquisitionMetadata): string {
  const parts: string[] = [];
  const px = formatPixelSize(md.pixel_size_nm);
  if (px) parts.push(px);
  if (md.kV !== undefined) parts.push(`${formatNum(md.kV)} kV`);
  if (md.detector) parts.push(md.signal ? `${md.detector} ${md.signal}` : md.detector);
  return parts.join(" • ");
}

function formatPixelSize(nm: number | undefined): string | null {
  if (nm === undefined || nm === null) return null;
  if (nm >= 1000) return `${(nm / 1000).toFixed(2)} µm/px`;
  if (nm >= 10) return `${nm.toFixed(1)} nm/px`;
  if (nm >= 1) return `${nm.toFixed(2)} nm/px`;
  return `${nm.toFixed(3)} nm/px`;
}

function formatFOV(hfw: number | undefined, vfw: number | undefined): string | null {
  if (hfw === undefined && vfw === undefined) return null;
  if (hfw !== undefined && vfw !== undefined) {
    return `${formatNum(hfw)} × ${formatNum(vfw)} µm`;
  }
  return `${formatNum((hfw ?? vfw)!)} µm`;
}

function formatNum(n: number): string {
  if (n >= 100) return n.toFixed(0);
  if (n >= 10) return n.toFixed(1);
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(3);
}

function labelForSource(source: MetadataSource): string {
  switch (source) {
    case "extracted":
      return "extracted from FEI tags";
    case "manual":
      return "user-entered";
    case "partial":
      return "extracted + user-supplemented";
    default:
      return "unknown";
  }
}

/**
 * Small pill that visually distinguishes manual / partial metadata from
 * the FEI-extracted default. Returns null for extracted/none — no decoration
 * needed (extracted is the assumed baseline; none already shows a separate warning).
 */
function SourceBadge({ source }: { source: MetadataSource }) {
  if (source !== "manual" && source !== "partial") return null;

  const label = source === "manual" ? "manual" : "edited";
  const tooltip =
    source === "manual"
      ? "Entered manually — not from instrument tags"
      : "Extracted from instrument tags then edited by a user";

  return (
    <Tooltip title={tooltip}>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          fontSize: "0.55rem",
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          px: 0.5,
          py: 0.05,
          borderRadius: 0.5,
          bgcolor: source === "manual" ? "info.main" : "warning.main",
          color: "#fff",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}
